/**
 * Version history + activity — pure model layer for the History drawer.
 *
 * Types mirror the polymorphic `version_snapshots` / `audit_events` tables.
 * Helpers are deliberately Supabase-free and React-free so they unit-test in
 * isolation. Consumed by `useVersionHistoryVM` and `HistoryDrawer`.
 */

export type SubjectKind = 'document' | 'work_item';

export type AuditEventType =
  | 'property_changed'
  | 'lifecycle_changed'
  | 'status_changed'
  | 'parent_changed'
  | 'subtype_changed'
  | 'title_changed'
  | 'body_changed'
  | 'comment_added'
  | 'comment_edited'
  | 'comment_deleted'
  | 'comment_resolved'
  | 'comment_reopened';

/** One row of `version_snapshots` — a title/body content snapshot. */
export interface VersionSnapshot {
  id: string;
  subjectKind: SubjectKind;
  subjectId: string;
  versionNumber: number;
  title: string;
  body: string;
  snapshotAt: string;
  snapshotBy: string | null;
  sourceVersionId: string | null;
}

/** One row of `audit_events` — a non-content field change. */
export interface ActivityEvent {
  id: string;
  subjectKind: SubjectKind;
  subjectId: string;
  eventType: AuditEventType;
  payload: Record<string, unknown> | null;
  actorId: string | null;
  at: string;
}

/** Newest first — by version number descending. Does not mutate the input. */
export function sortSnapshots(
  snapshots: readonly VersionSnapshot[]
): VersionSnapshot[] {
  return [...snapshots].sort((a, b) => b.versionNumber - a.versionNumber);
}

/** Character length of a snapshot's body — drives the History "Length" column. */
export function snapshotLength(snapshot: VersionSnapshot): number {
  return snapshot.body.length;
}

/**
 * True only for JSON scalars (string / number / boolean) that are safe to
 * interpolate into a changelog line. Objects, arrays, null and undefined are
 * rejected — `String()` on an object yields the literal `"[object Object]"`,
 * which must never reach the UI.
 */
function isRenderableScalar(v: unknown): v is string | number | boolean {
  return (
    typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean'
  );
}

/**
 * Resolves a payload field to a display string, or `null` when the value is
 * absent or non-scalar. Callers omit the value entirely when this returns
 * `null` rather than printing a raw object.
 */
function scalarField(
  payload: Record<string, unknown> | null,
  key: string
): string | null {
  if (!payload) return null;
  const v = payload[key];
  if (v === null || v === undefined || v === '') return null;
  if (!isRenderableScalar(v)) return null;
  return String(v);
}

/**
 * Renders `<verb> from <a> to <b>` only when BOTH endpoints are renderable
 * scalars; otherwise falls back to the bare `prefix` so structured (object)
 * payloads never leak `[object Object]` into the feed.
 */
function withFromTo(
  prefix: string,
  payload: Record<string, unknown> | null
): string {
  const from = scalarField(payload, 'from');
  const to = scalarField(payload, 'to');
  if (from !== null && to !== null) {
    return `${prefix} from ${from} to ${to}`;
  }
  return prefix;
}

/**
 * Renders a single human-readable Activity changelog line, e.g.
 * `Ada changed status from Backlog to In Progress`. `actorHandle` is the
 * resolved display name, or null when unknown (falls back to "Someone").
 *
 * Never emits `[object Object]`: payload `from`/`to` values that are objects
 * or arrays (work-item date changes, document attribute changes) are omitted
 * and the line degrades to a clean generic phrasing.
 */
export function formatActivityEvent(
  event: ActivityEvent,
  actorHandle: string | null
): string {
  const who = actorHandle ?? 'Someone';
  const p = event.payload;
  const kind = p && typeof p.kind === 'string' ? p.kind : null;

  // `property_changed` payloads are discriminated by `kind` (see the
  // work-item / document mutation RPCs). Handle each before the generic path.
  if (kind === 'created') return `${who} created this`;
  if (kind === 'archived') return `${who} archived this`;
  if (kind === 'restored') return `${who} restored this`;
  if (kind === 'dates') {
    // from/to are {start_date,end_date,due_date} objects — never scalars.
    return `${who} updated the dates`;
  }
  if (kind === 'attribute_changed') {
    // from/to are arbitrary jsonb (often objects); property_def_id is a UUID
    // not resolvable here. Include from/to only when both are scalars.
    return withFromTo(`${who} changed a property`, p);
  }
  if (kind === 'priority') {
    return withFromTo(`${who} changed priority`, p);
  }
  if (kind === 'assignee') {
    // from/to are member UUIDs — not human-readable here, so omit them.
    const to = p ? p.to : undefined;
    return to === null || to === undefined
      ? `${who} unassigned this`
      : `${who} changed the assignee`;
  }
  if (kind === 'type_changed') {
    // from/to are document_type UUIDs — not human-readable here.
    return `${who} changed the type`;
  }

  switch (event.eventType) {
    case 'status_changed':
      return withFromTo(`${who} changed status`, p);
    case 'lifecycle_changed':
      return withFromTo(`${who} changed lifecycle`, p);
    case 'subtype_changed':
      return `${who} changed the type`;
    case 'parent_changed': {
      // from/to are work-item UUIDs — never print them. Use null-ness of `to`
      // to distinguish set / clear / change.
      const from = p ? p.from : undefined;
      const to = p ? p.to : undefined;
      const hasFrom = from !== null && from !== undefined && from !== '';
      const hasTo = to !== null && to !== undefined && to !== '';
      if (!hasTo) return `${who} cleared the parent`;
      if (!hasFrom) return `${who} set the parent`;
      return `${who} changed the parent`;
    }
    case 'title_changed':
      return `${who} changed the title`;
    case 'body_changed':
      return `${who} changed the body`;
    case 'property_changed':
      return withFromTo(`${who} changed a property`, p);
    case 'comment_added':
      return `${who} added a comment`;
    case 'comment_edited':
      return `${who} edited a comment`;
    case 'comment_deleted':
      return `${who} deleted a comment`;
    case 'comment_resolved':
      return `${who} resolved a comment`;
    case 'comment_reopened':
      return `${who} reopened a comment`;
    default:
      return `${who} made a change`;
  }
}

export const EVENT_TYPE_LABELS: Record<AuditEventType, string> = {
  property_changed: 'Property changed',
  lifecycle_changed: 'Lifecycle changed',
  status_changed: 'Status changed',
  parent_changed: 'Parent changed',
  subtype_changed: 'Type changed',
  title_changed: 'Title changed',
  body_changed: 'Body changed',
  comment_added: 'Comment added',
  comment_edited: 'Comment edited',
  comment_deleted: 'Comment deleted',
  comment_resolved: 'Comment resolved',
  comment_reopened: 'Comment reopened'
};
